import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logoutUserSession, readUserSession } from '../Auth/userSession';
import {
    AppBar,
    Toolbar,
    Typography,
    Container,
    Stack,
    Button,
    Box,
    useMediaQuery,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Collapse,
} from '@mui/material';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
// 아이콘
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import logo from '../../assets/GARAM_03.png';

const assets = {
    logo: logo,
};

// --- 1. 테마 설정 (반응형 폰트 적용) ---
let theme = createTheme({
    palette: {
        primary: { main: '#005a8d' }, // 가람마인드 네이비 블루
        secondary: { main: '#e3f2fd' },
        text: { primary: '#1a1a1a', secondary: '#555555' },
        background: { default: '#ffffff' },
    },
    typography: {
        fontFamily: '"Pretendard", "Noto Sans KR", sans-serif',
        h3: { fontWeight: 700, lineHeight: 1.3 },
        h4: { fontWeight: 700, lineHeight: 1.35 },
        h5: { fontWeight: 600 },
        subtitle1: { lineHeight: 1.6 },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: '50px', padding: '10px 24px', fontWeight: 600 },
            },
        },
        MuiContainer: {
            defaultProps: { maxWidth: 'lg' }, // 기본 컨테이너 너비 설정
        },
    },
});
// 폰트 크기 자동 조절 (모바일에서 너무 크지 않게)
theme = responsiveFontSizes(theme);

const Header = () => {
    const navigate = useNavigate();
    const [loggingOut, setLoggingOut] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // MD(900px) 이하일 때 true
    const [mobileOpen, setMobileOpen] = useState(false); // 모바일 메뉴 토글 상태
    const [practiceAnchorEl, setPracticeAnchorEl] = useState(null); // 데스크톱 Practice 드롭다운
    const [mobilePracticeOpen, setMobilePracticeOpen] = useState(false); // 모바일 Practice 서브메뉴 토글
    const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(readUserSession())); // 로그인 상태
    useEffect(() => {
        const handleAuthChange = () => setIsLoggedIn(Boolean(readUserSession()));
        window.addEventListener('garam-auth-change', handleAuthChange);
        return () => window.removeEventListener('garam-auth-change', handleAuthChange);
    }, []);

    async function handleLogout() {
        if (loggingOut) return;
        setLoggingOut(true);
        try {
            await logoutUserSession();
            navigate('/');
        } catch {
            window.alert('로그아웃하지 못했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setLoggingOut(false);
        }
    }

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handlePracticeMenuOpen = (event) => {
        setPracticeAnchorEl(event.currentTarget);
    };

    const handlePracticeMenuClose = () => {
        setPracticeAnchorEl(null);
    };

    const baseNavItems = [
        { title: 'News', path: '/news' },
        { title: 'About', path: '/about' },
        { title: 'Service', path: '/service' }, // 말랑포켓 소개서 넣은 후 활성화
        { title: 'Lecture', path: '/lecture' },
        {
            title: 'Practice',
            children: [
                { title: 'AI 내담자', path: '/ai-clients' },
                { title: '반영 연습', path: '/reflection-practice' },
            ],
        },
        { title: 'Solution', path: '/solution' },
        { title: 'Contact', path: '/contact' },
    ];

    const authNavItems = isLoggedIn
        ? [
            { title: loggingOut ? '로그아웃 중…' : 'Logout', action: handleLogout },
            { title: 'My Page', path: '/mypage' },
        ]
        : [
            { title: 'Login', path: '/login' },
            { title: 'Sign Up', path: '/register' },
        ];

    const navItems = [...baseNavItems, ...authNavItems];

    return (
        <>
        <AppBar
            position="sticky"
            elevation={3} // 하얀 배경일 때는 1 정도의 그림자가 있어야 본문과 구분됩니다.
            sx={{
                bgcolor: 'white', // 배경을 하얗게 설정
                top: 0,
                zIndex: 1100, // 다른 요소보다 위에 있도록 설정
            }}
        >
            <Container>
                <Toolbar
                    disableGutters
                    sx={{ height: { xs: 60, md: 80 }, justifyContent: 'space-between' }}
                >
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link to="/" onClick={() => window.scrollTo(0, 0)} style={{ display: 'flex', textDecoration: 'none' }}>
                            {assets.logo ? (
                                <img
                                    src={assets.logo}
                                    alt="Garam Mind"
                                    style={{ height: isMobile ? 50 : 70 }}
                                />
                            ) : (
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                    GARAM MIND
                                </Typography>
                            )}
                        </Link>
                    </Box>

                    {/* Desktop Menu */}
                    {!isMobile && (
                        <Stack direction="row" spacing={1}>
                            {navItems.map((item) =>
                                item.children ? (
                                    <Box key={item.title}>
                                        <Button
                                            onClick={handlePracticeMenuOpen}
                                            color="inherit"
                                            endIcon={<KeyboardArrowDownIcon />}
                                            sx={{ color: '#333', fontWeight: 700 }}
                                        >
                                            {item.title}
                                        </Button>
                                        <Menu
                                            anchorEl={practiceAnchorEl}
                                            open={Boolean(practiceAnchorEl)}
                                            onClose={handlePracticeMenuClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        >
                                            {item.children.map((child) => (
                                                <MenuItem
                                                    key={child.title}
                                                    component={Link}
                                                    to={child.path}
                                                    onClick={() => {
                                                        handlePracticeMenuClose();
                                                        window.scrollTo(0, 0);
                                                    }}
                                                >
                                                    {child.title}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Box>
                                ) : (
                                    <Button
                                        key={item.title}
                                        component={item.path ? Link : 'button'}
                                        disabled={Boolean(item.action) && loggingOut}
                                        to={item.path}
                                        onClick={() => { if (item.action) item.action(); else window.scrollTo(0, 0); }}
                                        color="inherit"
                                        sx={{ color: '#333', fontWeight: 700 }}
                                    >
                                        {item.title}
                                    </Button>
                                )
                            )}
                        </Stack>
                    )}

                    {/* Mobile Menu Icon */}
                    {isMobile && (
                        <IconButton color="default" edge="end" onClick={handleDrawerToggle}>
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </Container>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
            }}
        >
            <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ my: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    GARAM MIND
                </Typography>
                <List>
                    {navItems.map((item) =>
                        item.children ? (
                            <React.Fragment key={item.title}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMobilePracticeOpen(!mobilePracticeOpen);
                                        }}
                                        sx={{ textAlign: 'center', justifyContent: 'center' }}
                                    >
                                        <ListItemText primary={item.title} primaryTypographyProps={{ fontWeight: 600, color: '#333' }} />
                                        {mobilePracticeOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </ListItemButton>
                                </ListItem>
                                <Collapse in={mobilePracticeOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.children.map((child) => (
                                            <ListItem key={child.title} disablePadding>
                                                <ListItemButton
                                                    component={Link}
                                                    to={child.path}
                                                    sx={{ textAlign: 'center', pl: 4 }}
                                                    onClick={() => window.scrollTo(0, 0)}
                                                >
                                                    <ListItemText primary={child.title} primaryTypographyProps={{ fontWeight: 500, color: '#555' }} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        ) : (
                            <ListItem key={item.title} disablePadding>
                                <ListItemButton
                                    component={item.path ? Link : 'button'}
                                        disabled={Boolean(item.action) && loggingOut}
                                    to={item.path}
                                    sx={{ textAlign: 'center' }}
                                    onClick={() => { if (item.action) item.action(); else window.scrollTo(0, 0); }}
                                >
                                    <ListItemText primary={item.title} primaryTypographyProps={{ fontWeight: 600, color: '#333' }} />
                                </ListItemButton>
                            </ListItem>
                        )
                    )}
                </List>
            </Box>
        </Drawer>
        </>
    );
};

export default Header;
